import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  RefreshIcon,
  Text,
  Link,
  PaperclipIcon,
  Amount,
  useToast,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { DetailsViewSkeleton } from 'components/SalaryComponents/components/details/DetailsViewHelpers';
import { EmptyView } from '../../../components/EmptyView';
import { getStatusColor, getStatusLabel, formatDate, generateFile } from './utils';
import { PDFViewer } from 'components/ui/PDFViewer';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import api from 'api';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import pluralize from 'pluralize';
import { paiseAmountToRupee } from 'utils/Numbers';

export const ClaimsDetailView = () => {
  const { paramId: claimId, closeDetailsView } = useDetailsView('claimId');

  const isOpen = !!claimId;

  const toast = useToast();

  const getClaimData = useQuery({
    queryKey: [api.flexibleBenefitComponents.queries.getFlexiReimbursementClaim, { claimId }],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexiReimbursementClaim({ claim_id: claimId || '' }),
    enabled: !!claimId,
    retry: false,
    select: (data) => data?.payrollGetFlexiReimbursementClaim?.claim,
    onError: (error: Error) => {
      toast.show({
        content: error.message || 'Error fetching claim data',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const getUFHFiles = useQuery({
    queryKey: [
      api.flexibleBenefitComponents.queries.getClaimEvidenceFiles,
      getClaimData.data?.claim_evidence_id,
    ],
    queryFn: () => {
      return api.flexibleBenefitComponents.queries.getClaimEvidenceFiles({
        file_requests:
          getClaimData.data?.claim_evidence_id.map((id: string) => ({ file_id: id })) || [],
      });
    },
    enabled:
      !!getClaimData?.data?.claim_evidence_id && getClaimData?.data?.claim_evidence_id?.length > 0,
    select: (data) =>
      data?.payrollGetBatchSignedURLs?.data.map((file: any) =>
        generateFile(
          file.size,
          `${file.name}.${file.extension}`,
          file.extension,
          file.signed_url,
          file.file_id,
        ),
      ),
    onSuccess: (data: File[]) => {
      return data;
    },
    onError: (error: Error) => {
      toast.show({
        content: error.message || 'Error fetching UFH files',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const data = useMemo(() => {
    if (!getClaimData.data) return null;
    return {
      ...getClaimData.data,
      files: getUFHFiles.data || [],
    };
  }, [getClaimData.data, getUFHFiles.data]);

  const isLoading = getClaimData.isLoading || getUFHFiles.isLoading;
  const isError = getClaimData.isError;

  const refetch = () => {
    getClaimData.refetch();
  };

  const onDismiss = () => {
    closeDetailsView();
  };

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const onPreviewClose = () => {
    setIsPreviewOpen(false);
  };

  const history = useHistory();
  const isStatusApproved =
    data?.claim_status === 'APPROVED' || data?.claim_status === 'PARTIALLY_APPROVED';

  const isStatusRejected = data?.claim_status === 'REJECTED';
  const isStatusRejectedOrApproved = isStatusRejected || isStatusApproved;

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
        <DrawerHeader
          color={getStatusColor(data?.claim_status || 'PENDING')}
          title={data?.component_display_name || 'Claim Details'}
          subtitle={data?.employee_name ? `Employee Name: ${data.employee_name}` : undefined}
        />
        <DrawerBody>
          {isLoading && <DetailsViewSkeleton onDismiss={onDismiss} />}
          {isError && (
            <EmptyView
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  Due to an unknown error, we could not load the claim details.
                </Text>
              }>
              <Box display="flex" justifyContent="center">
                <Box width="104px">
                  <Button icon={RefreshIcon} size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                </Box>
              </Box>
            </EmptyView>
          )}
          {data && !isLoading && !isError && (
            <Box display="flex" flexDirection="column" gap="spacing.6" flex="1">
              {/* Claim Status Section - Only for rejected and approved claims */}
              {isStatusRejectedOrApproved && (
                <Box display="flex" flexDirection="column">
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="spacing.3"
                    paddingY="spacing.4"
                    paddingX="spacing.6"
                    borderColor="surface.border.gray.muted"
                    borderWidth="thinner"
                    borderTopLeftRadius="xlarge"
                    borderTopRightRadius="xlarge"
                    borderBottomRightRadius="none"
                    borderBottomLeftRadius="none">
                    <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                      Claim Status
                    </Text>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="spacing.4"
                    paddingTop="spacing.4"
                    paddingBottom="spacing.6"
                    paddingX="spacing.6"
                    borderColor="surface.border.gray.muted"
                    borderTopWidth="none"
                    borderRightWidth="thinner"
                    borderBottomWidth="thinner"
                    borderLeftWidth="thinner"
                    borderTopLeftRadius="none"
                    borderTopRightRadius="none"
                    borderBottomRightRadius="xlarge"
                    borderBottomLeftRadius="xlarge">
                    <Box display="flex" flexDirection="column" gap="spacing.4">
                      <Box display="flex" flexDirection="column" gap="spacing.2">
                        <Text size="small" weight="regular" color="surface.text.gray.muted">
                          Status
                        </Text>
                        <Badge color={getStatusColor(data.claim_status)}>
                          {getStatusLabel(data.claim_status)}
                        </Badge>
                      </Box>
                      {isStatusRejected && (
                        <>
                          <Box display="flex" flexDirection="column" gap="spacing.2">
                            <Text size="small" weight="regular" color="surface.text.gray.muted">
                              Updated date
                            </Text>
                            <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                              {formatDate(data?.updated_at)}
                            </Text>
                          </Box>
                          {data.approver_user_name && (
                            <Box display="flex" flexDirection="column" gap="spacing.2">
                              <Text size="small" weight="regular" color="surface.text.gray.muted">
                                Rejected by
                              </Text>
                              <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                                {data.approver_user_name || '--'}
                              </Text>
                            </Box>
                          )}
                          {data.claim_comments && (
                            <Box display="flex" flexDirection="column" gap="spacing.2">
                              <Text size="small" weight="regular" color="surface.text.gray.muted">
                                Rejection reason
                              </Text>
                              <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                                {data.claim_comments}
                              </Text>
                            </Box>
                          )}
                        </>
                      )}
                      {isStatusApproved && (
                        <>
                          <Box display="flex" flexDirection="column" gap="spacing.2">
                            <Text size="small" weight="regular" color="surface.text.gray.muted">
                              Updated date
                            </Text>
                            <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                              {formatDate(data?.updated_at)}
                            </Text>
                          </Box>
                          {data.approver_user_name && (
                            <Box display="flex" flexDirection="column" gap="spacing.2">
                              <Text size="small" weight="regular" color="surface.text.gray.muted">
                                Approved by
                              </Text>
                              <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                                {data.approver_user_name || '--'}
                              </Text>
                            </Box>
                          )}
                          {data.approved_amount > 0 && (
                            <Box display="flex" flexDirection="column" gap="spacing.2">
                              <Text size="small" weight="regular" color="surface.text.gray.muted">
                                Approved Amount
                              </Text>
                              <Amount
                                size="medium"
                                weight="semibold"
                                value={paiseAmountToRupee(data.approved_amount)}
                              />
                            </Box>
                          )}
                          {data.claim_comments && (
                            <Box display="flex" flexDirection="column" gap="spacing.2">
                              <Text size="small" weight="regular" color="surface.text.gray.muted">
                                Comments
                              </Text>
                              <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                                {data.claim_comments}
                              </Text>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                    {isStatusRejected && (
                      <Button
                        isFullWidth
                        variant="secondary"
                        size="medium"
                        onClick={() => {
                          history.push(
                            {
                              pathname: routePaths.flexibleBenefits.claims.edit,
                              search: `claimId=${data.claim_id}&userId=${
                                data.employee_id?.toString() || ''
                              }`,
                            },
                            {
                              claimId: data.claim_id,
                            },
                          );
                        }}>
                        Modify claim
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="spacing.3"
                  paddingY="spacing.4"
                  paddingX="spacing.6"
                  borderColor="surface.border.gray.muted"
                  borderWidth="thinner"
                  borderTopLeftRadius="xlarge"
                  borderTopRightRadius="xlarge"
                  borderBottomRightRadius="none"
                  borderBottomLeftRadius="none">
                  <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                    Claim details
                  </Text>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="spacing.4"
                  paddingTop="spacing.4"
                  paddingBottom="spacing.6"
                  paddingX="spacing.6"
                  borderColor="surface.border.gray.muted"
                  borderTopWidth="none"
                  borderRightWidth="thinner"
                  borderBottomWidth="thinner"
                  borderLeftWidth="thinner"
                  borderTopLeftRadius="none"
                  borderTopRightRadius="none"
                  borderBottomRightRadius="xlarge"
                  borderBottomLeftRadius="xlarge">
                  <Box display="flex" flexDirection="column" gap="spacing.4">
                    <Box display="flex" flexDirection="column" gap="spacing.2">
                      <Text size="small" weight="regular" color="surface.text.gray.muted">
                        Claimed Amount
                      </Text>
                      <Amount
                        size="medium"
                        weight="semibold"
                        value={Number(paiseAmountToRupee(data.claim_amount).toString())}
                      />
                    </Box>
                    <Box display="flex" flexDirection="column" gap="spacing.2">
                      <Text size="small" weight="regular" color="surface.text.gray.muted">
                        Description
                      </Text>
                      <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                        {data.claim_description || '--'}
                      </Text>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="spacing.2">
                      <Text size="small" weight="regular" color="surface.text.gray.muted">
                        Expense date
                      </Text>
                      <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                        {data.expense_date
                          ? getFormattedDateValue({
                              date: new Date(
                                Number(data.expense_date) > 10000000000
                                  ? Number(data.expense_date)
                                  : Number(data.expense_date) * 1000,
                              ),
                              formatString: dateFormats.dateShortMonthCommaYear,
                            })
                          : '--'}
                      </Text>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="spacing.2">
                      <Text size="small" weight="regular" color="surface.text.gray.muted">
                        Created on
                      </Text>
                      <Text size="medium" weight="medium" color="surface.text.gray.subtle">
                        {formatDate(Number(data?.created_at))}
                      </Text>
                    </Box>
                    {data.claim_evidence_id.length > 0 && (
                      <Box display="flex" flexDirection="column" gap="spacing.2">
                        <Text size="small" weight="regular" color="surface.text.gray.muted">
                          Proof
                        </Text>
                        <Link
                          iconPosition="left"
                          icon={PaperclipIcon}
                          onClick={() => {
                            setIsPreviewOpen(true);
                          }}>
                          {`${data.claim_evidence_id.length} ${pluralize(
                            'attachment',
                            data.claim_evidence_id.length,
                          )}`}
                        </Link>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DrawerBody>
      </Drawer>
      <PDFViewer isOpen={isPreviewOpen} files={data?.files || []} onClose={onPreviewClose} />
    </>
  );
};
